<template>
  <div>
    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-card>
        <v-card-title class="pb-0 px-3">
          <span class="gc-text-h6">{{
            `${lang.memoriesWall.follow} ${activePerson.firstName} ${activePerson.lastName}`
          }}</span>
        </v-card-title>
        <v-card-text class="px-3">
          <label class="caption">{{ lang.memoriesWall.w1 }}</label>
          <div class="mx-0 pt-4 input-group">
            <label>{{ lang.fields.description }} *</label>
            <textarea v-model="description" rows="4"></textarea>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            elevation="0"
            small
            text
            @click="closeDialogFollowPerson()"
          >
            {{ lang.actions.close }}
          </v-btn>
          <v-btn
            class="btn-bg"
            small
            elevation="0"
            :disabled="description == ''"
            @click="followPerson()"
          >
            {{ lang.actions.save }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-row class="page-title-container">
      <v-col class="col-12">
        <h2 class="page-title">{{ lang.menuLinks.memoriesWall }}</h2>
      </v-col>

      <v-col class="col-12" v-if="loading">
        <v-skeleton-loader
          elevation="0"
          type="table-heading, actions"
        ></v-skeleton-loader>
      </v-col>
      <v-col v-else class="col-12 pb-1 pt-2">
        <div class="filter-container">
          <div class="filter-grid" :class="{ isPet: type }">
            <div class="input-group">
              <input
                type="text"
                :disabled="loading"
                :placeholder="lang.fields.name"
                @keyup.enter="getList(true)"
                v-model="listPayload.name"
              />
            </div>

            <div class="input-group" v-if="!type">
              <input
                type="text"
                @keyup.enter="getList(true)"
                :disabled="loading"
                :placeholder="lang.fields.surname"
                v-model="listPayload.surname"
              />
            </div>
            <div class="switch-group">
              <label class="switch-label">
                <span :class="{ active: !type }">{{ lang.fields.human }}</span>
                <div class="switch">
                  <input
                    type="checkbox"
                    v-model="type"
                    @change="getList(true)"
                  />
                  <span class="slider"></span>
                </div>
                <span :class="{ active: type }">{{ lang.fields.pets }}</span>
              </label>
            </div>

            <button
              class="search-button btn-bg"
              @click="getList(true)"
              :disabled="loading"
            >
              <span class="mdi mdi-magnify"></span>
              {{ lang.actions.search }}
            </button>
          </div>
        </div>
      </v-col>
      <v-col class="col-12" v-if="loading">
        <v-skeleton-loader
          :elevation="0"
          class="mx-auto"
          type="table-tbody, table-tfoot"
        ></v-skeleton-loader>
      </v-col>
      <v-col class="col-12" v-if="!loading">
        <div class="mb-4">
          <v-btn
            fab
            small
            elevation="0"
            :class="{ 'btn-bg': view == 'table', inactive: view != 'table' }"
          >
            <v-icon @click="view = 'table'">mdi-view-list</v-icon>
          </v-btn>
          <v-btn
            fab
            small
            elevation="0"
            class="ml-2"
            :class="{ 'btn-bg': view == 'card', inactive: view != 'card' }"
          >
            <v-icon @click="view = 'card'">mdi-card-account-details</v-icon>
          </v-btn>
        </div>

        <v-row v-if="view == 'table' && listOfPersons.length" class="mt-n2">
          <v-col class="mobile-px-0">
            <div class="table-container">
              <div class="responsive-table">
                <div
                  v-for="deceased in listOfPersons"
                  :key="deceased.id"
                  class="table-row"
                >
                  <div class="profile-section">
                    <v-img
                      v-if="deceased.profileImageUrl"
                      alt="Profile image"
                      dark
                      :lazy-src="require('../assets/images/lazy-loading.png')"
                      class="profile-pic"
                      :src="deceased.profileImageUrl"
                    ></v-img>
                    <v-img
                      alt="Profile image"
                      :lazy-src="require('../assets/images/lazy-loading.png')"
                      dark
                      class="profile-pic"
                      v-else
                      :src="getUserProfileImage(deceased.gender)"
                    ></v-img>
                    <div class="profile-details">
                      <v-icon class="star-icon" color="primary" v-if="deceased.userId == currentUser.id">mdi-star</v-icon>
                      <h3 v-if="!type">
                        {{ `${deceased.firstName} ${deceased.lastName}` }}
                        <p class="caption mb-0" v-if="deceased.nickname">
                          {{ deceased.nickname }}
                        </p>
                      </h3>
                      <h3 v-else>{{ deceased.name }}</h3>
                      <span class="mobile-dates"
                        >{{ deceased.dateOfBirth | shortDate }} -
                        {{ deceased.dateOfDeath | shortDate }}</span
                      >
                    </div>
                  </div>

                  <div class="details-section">
                    <div class="detail-item desktop-only">
                      <div class="detail-header">
                        {{ lang.memoriesWall.from }}
                      </div>
                      <div class="detail-content">
                        {{ `${deceased.city}, ${deceased.country}` }}
                      </div>
                    </div>

                    <div class="detail-item desktop-only">
                      <div class="detail-header">
                        {{ lang.memoriesWall.birthDate }}
                      </div>
                      <div class="detail-content">
                        {{ deceased.dateOfBirth | shortDate }}
                      </div>
                    </div>

                    <div class="detail-item desktop-only">
                      <div class="detail-header">
                        {{ lang.memoriesWall.deathDate }}
                      </div>
                      <div class="detail-content">
                        {{ deceased.dateOfDeath | shortDate }}
                      </div>
                    </div>
                  </div>

                  <div class="action-section">
                    <div class="show-mobile justify-end">
                      <v-btn
                        style="min-width: 40px"
                        :outlined="![1, 2].includes(deceased.relationFlag)"
                        :class="{
                          'btn-bg': [1, 2].includes(deceased.relationFlag),
                          'primary--text': [3].includes(deceased.relationFlag),
                          'primary--text': [0].includes(deceased.relationFlag),
                        }"
                        :disabled="deceased.relationFlag == 3"
                        elevation="0"
                        @click="executeAction(deceased)"
                      >
                        <v-icon v-if="deceased.relationFlag == 0"
                          >mdi-plus</v-icon
                        >
                        <v-icon v-else-if="deceased.relationFlag == 1"
                          >mdi-account</v-icon
                        >
                        <v-icon v-else-if="deceased.relationFlag == 2"
                          >mdi-account</v-icon
                        >
                        <v-icon v-else-if="deceased.relationFlag == 3"
                          >mdi-account-multiple-outline</v-icon
                        >
                        <v-icon v-else-if="deceased.relationFlag == 4"
                          >mdi-account-multiple-outline</v-icon
                        >
                      </v-btn>
                    </div>
                    <div class="justify-end align-center show-desktop">
                      <v-btn
                        style="min-width: 170px"
                        :outlined="![1, 2, 3].includes(deceased.relationFlag)"
                        :class="{
                          'btn-bg': [1, 2].includes(deceased.relationFlag),
                          'primary--text with-opacity': [3].includes(
                            deceased.relationFlag
                          ),
                          'primary--text': [0].includes(deceased.relationFlag),
                        }"
                        elevation="0"
                        :disabled="deceased.relationFlag == 3"
                        @click="executeAction(deceased)"
                        >{{ getActionName(deceased) }}</v-btn
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
          
          <v-col cols="12" v-if="listOfPersons.length" class="mobile-px-0">
            <v-pagination
              class="mt-4"
              v-model="listPayload.pageIndex"
              :length="listCount"
              :total-visible="6"
              prev-icon="mdi-menu-left"
              next-icon="mdi-menu-right"
              @input="getList()"
            ></v-pagination>
          </v-col>
        </v-row>

        <v-row class="mt-2 card-view" v-if="view == 'card' && listOfPersons.length">
          <v-col
            class="col-12 col-sm-6 col-md-4 col-lg-3 pt-2 pb-1 mobile-px-0"
            v-for="(deceased, index) in listOfPersons"
            :key="index"
          >
            <v-card
              style="
                width: 100%;
                box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
                border-radius: 12px;
              "
              class="py-2"
            >
              <v-card-text class="column-center text-center">
                <v-icon class="star-icon" color="primary" v-if="deceased.userId == currentUser.id">mdi-star</v-icon>
              
                <div class="rel mx-auto">
                  <v-img
                    v-if="deceased.profileImageUrl"
                    :alt="deceased.firstName + ' ' + deceased.lastName"
                    :lazy-src="require('../assets/images/lazy-loading.png')"
                    class="user-image"
                    aspect-ratio="1"
                    style="border-radius: 50%"
                    :src="deceased.profileImageUrl"
                  ></v-img>
                  <v-img
                    class="user-image"
                    :alt="deceased.firstName + ' ' + deceased.lastName"
                    :lazy-src="require('../assets/images/lazy-loading.png')"
                    aspect-ratio="1"
                    style="border-radius: 50%"
                    v-else
                    :src="getUserProfileImage(deceased.gender)"
                  ></v-img>
                </div>

                <div class="profile-details">
                  <h3
                    class="mt-4 d-flex flex-column"
                    :class="{
                      'mb-2': !deceased.nickname,
                      'mb-0': deceased.nickname,
                    }"
                  >
                    <span v-if="!type">{{
                      `${deceased.firstName} ${deceased.lastName}`
                    }}</span>
                    <span v-else>{{ `${deceased.name}` }}</span>
                  </h3>
                  <p class="caption mt-0 mb-2" v-if="deceased.nickname">
                    {{ `${deceased.nickname}` }}
                  </p>
                </div>
                <span class="caption">{{
                  getBirthToDeathDate(
                    deceased.dateOfBirth,
                    deceased.dateOfDeath
                  )
                }}</span>
                <div class="d-flex justify-center mt-4">
                  <v-btn
                    style="min-width: 170px"
                    :outlined="![1, 2, 3].includes(deceased.relationFlag)"
                    :class="{
                      'btn-bg': [1, 2].includes(deceased.relationFlag),
                      'primary--text with-opacity': [3].includes(
                        deceased.relationFlag
                      ),
                      'primary--text': [0].includes(deceased.relationFlag),
                    }"
                    elevation="0"
                    :disabled="deceased.relationFlag == 3"
                    @click="executeAction(deceased)"
                    >{{ getActionName(deceased) }}</v-btn
                  >
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" v-if="listOfPersons.length" class="mobile-px-0">
            <v-pagination
              class="mt-4"
              v-model="listPayload.pageIndex"
              :length="listCount"
              :total-visible="6"
              prev-icon="mdi-menu-left"
              next-icon="mdi-menu-right"
              @input="getList()"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-col>
      <v-col class="col-12 pt-0" v-if="!listOfPersons.length && !loading">
        <NoResult
          :show="!listOfPersons.length"
          class="mt-0"
          :title="lang.noResult.search"
          :button="{ link: '/AddMemories', text: lang.noResult.searchBtn }"
        ></NoResult>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import moment from "moment";
import NoResult from "../components/noResult";
import helpers from "../plugins/helpers.js";

export default {
  name: "MemoriesWall",
  data() {
    return {
      profileData: [
        {
          name: "John Doe",
          from: "New York, USA",
          birthDate: "Jan 15, 1940",
          deathDate: "Mar 20, 2022",
          image: "https://placekitten.com/100/100",
        },
        {
          name: "Jane Smith",
          from: "London, UK",
          birthDate: "Apr 3, 1935",
          deathDate: "Feb 12, 2023",
          image: "https://placekitten.com/101/101",
        },
        {
          name: "Robert Johnson",
          from: "Paris, France",
          birthDate: "Sep 28, 1942",
          deathDate: "Jan 5, 2024",
          image: "https://placekitten.com/102/102",
        },
      ],
      listPayload: {
        pageIndex: 1,
        limit: 12,
        isHuman: "Human",
        name: "",
        surname: "",
      },
      currentDeceased: {},
      hoveredIndex: null,
      description: "",
      dialog: false,
      activePerson: {},
      type: false,
    };
  },
  components: {
    NoResult,
  },
  computed: {
    view: {
      get() {
        return this.$store.state.memoriesWallView;
      },
      set(newValue) {
        this.$store.commit("setMemoriesWallView", newValue);
      },
    },
    name() {
      return this.data;
    },
    lang() {
      return this.$store.state.langObj || {};
    },
    ...mapGetters("user", ["currentUser", "mySendRequests"]),
    ...mapGetters("person", ["allPersons"]),
    ...mapState(["loading"]),
    listCount() {
      return this.allPersons.totalPages;
    },
    listOfPersons() {
      var mySendRequests = [];
      var allPersons = [];
      this.mySendRequests.forEach((element) => {
        mySendRequests.push(element.idPerson);
      });
      this.allPersons?.items?.forEach((element) => {
        if (mySendRequests.includes(element.id)) {
          element.requestSend = true;
        }
        allPersons.push(element);
      });

      return allPersons;
    },
  },
  methods: {
    getUserProfileImage(gender) {
      return helpers.getAvatarProfile(gender, this.listPayload.isHuman);
    },
    getBirthToDeathDate(birthDate, deathDate) {
      return `${moment(birthDate).format("DD/MM/YYYY")} - ${moment(
        deathDate
      ).format("DD/MM/YYYY")}`;
    },
    executeAction(deceased) {
      if (deceased.relationFlag == 0) {
        this.sendRequestModal(deceased);
      } else if (deceased.relationFlag == 1) {
        this.viewProfile(deceased);
      } else if (deceased.relationFlag == 2) {
        this.viewProfile(deceased);
      } else if (deceased.relationFlag == 3) {
        this.viewPendingRequests(deceased);
      } else if (deceased.relationFlag == 4) {
        this.viewPendingRequests(deceased);
      }
    },
    viewProfile(deceased) {
      this.$router.push(`Profile/${deceased.id}`);
    },
    viewPendingRequests() {
      this.$router.push(`PendingRequests`);
    },
    getActionName(deceased) {
      if (deceased.relationFlag == 0) {
        return this.lang.actions.sendRequest;
      } else if (deceased.relationFlag == 1) {
        return this.lang.actions.viewProfile;
      } else if (deceased.relationFlag == 2) {
        return this.lang.actions.viewProfile;
      } else if (deceased.relationFlag == 3) {
        return this.lang.actions.requestSent;
      } else if (deceased.relationFlag == 4) {
        return this.lang.actions.viewRequest;
      }
    },
    sendRequestModal(deceased) {
      this.activePerson = deceased;
      this.dialog = true;
    },
    checkIfOpen(id) {
      return id == this.currentDeceased.id;
    },
    getRangeDate(person) {
      return `${moment(person.dateOfBirth).format("MMM Do YYYY")} - ${moment(
        person.dateOfDeath
      ).format("MMM Do YYYY")}`;
    },
    followPerson() {
      var payload = {
        personId: this.activePerson.id,
        byUserId: this.currentUser.id,
        emails: [this.activePerson.userMail],
        description: this.description,
      };
      this.$store
        .dispatch("person/followPerson", payload)
        .then(() => {
          this.getList();
          this.closeDialogFollowPerson();
        })
        .catch(() => {
          this.getList();
          this.closeDialogFollowPerson();
        });
    },
    closeDialogFollowPerson() {
      this.activePerson = {};
      this.currentDeceased = {};
      this.description = "";
      this.dialog = false;
    },
    getList(resetPage = false) {
      if (resetPage) {
        this.listPayload.pageIndex = 1;
        this.currentDeceased = {};
      }
      this.listPayload.isHuman = !this.type;
      this.$store.dispatch("person/getAllPersons", this.listPayload);
    },
  },
  mounted() {
    document.title = "Good Casper - Memories Wall";
    this.getList(true);
  },
};
</script>

<style lang="scss">
.view-profile {
  cursor: pointer;
}
.with-opacity {
  opacity: 0.5;
}
.no-theme {
  @import "../assets/scss/_variables";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-default {
  @import "../assets/scss/_variables";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-black {
  @import "../assets/scss/_variables_black";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-blue-gray {
  @import "../assets/scss/_variables_blue_grey";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-blue {
  @import "../assets/scss/_variables_blue";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-cyan {
  @import "../assets/scss/_variables_cyan";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-dark-red {
  @import "../assets/scss/_variables_dark_red";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-green {
  @import "../assets/scss/_variables_light_green";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-light-green {
  @import "../assets/scss/_variables_green";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-light-brown {
  @import "../assets/scss/_variables_light_brown";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-lime-dark {
  @import "../assets/scss/_variables_lime_dark";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-orange {
  @import "../assets/scss/_variables_orange";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-pink {
  @import "../assets/scss/_variables_pink";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-light-purple {
  @import "../assets/scss/_variables_purple_light";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}

.theme-purple {
  @import "../assets/scss/_variables_purple";
  .custom-state .v-input--selection-controls__input div {
    color: $color-primary;
  }
}
.v-pagination button {
  box-shadow: none;
}
.mobile-icon {
  display: none;
}
.gender-card {
  position: absolute !important;
  padding: 10px;
  top: 10px;
  left: 10px;
  z-index: 99;
}
.close-card {
  position: absolute !important;
  padding: 10px;
  cursor: pointer;
  top: 10px;
  right: 10px;
  z-index: 99;
}
.show-fixed-card {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 99;
  .display-2-text {
    font-size: 48px !important;
    margin-top: 35px;
  }
  .display-1-text {
    font-size: 35px !important;
    margin-bottom: 25px;
  }
  .subtitle-2 {
    margin-bottom: 5px !important;
  }
}
.v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state)
  > .v-input__control
  > .v-input__slot
  fieldset {
  color: rgb(219, 219, 219) !important;
  border: 2px solid rgb(219, 219, 219) !important;
}
.v-text-field--outlined {
  input {
    color: rgb(65, 65, 65) !important;
  }
  .v-label {
    color: rgb(151, 151, 151) !important;
  }
  .v-select__selections {
    color: rgb(65, 65, 65) !important;
  }
}
.with-shadow {
  box-shadow: 0 0 4px 1px #ccd6e7 !important;
}
.v-sheet.rounded-bl-xl.v-card {
  border-radius: 0;
}
.gc-filters {
  background: #fff;
  padding: 25px 15px;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
  display: flex !important;
  flex-wrap: wrap;
  .v-card__progress {
    flex: 0 0 100%;
    position: absolute;
    bottom: 0;
  }
  > div {
    padding-right: 20px;
    &:first-child {
      flex: 1 0 30%;
    }
    &:nth-child(2) {
      flex: 1 0 30%;
    }
    &:nth-child(3) {
      flex: 0 0 175px;
    }
    &:nth-child(4) {
      flex: 0 0 130px;
    }
    &:last-child {
      text-align: right;
      padding-right: 0;
    }
  }
}
.gc-table {
  padding: 0 0px;
  border-radius: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  p.table-column-title {
    font-size: 14px !important;
    color: rgb(168, 168, 168);
    margin-bottom: 3px;
    font-weight: 100;
  }
  &--row {
    &--column {
      cursor: default;
      font-weight: bold;
      padding-right: 10px;
      &:first-child {
        flex: 0 0 30%;
      }
      &:nth-child(2) {
        flex: 0 0 20%;
      }
      &:nth-child(3) {
        flex: 0 0 12.5%;
      }
      &:nth-child(4) {
        flex: 0 0 12.5%;
      }
      &:last-child {
        text-align: right;
        flex: 0 0 25%;
        padding-right: 0;
      }
    }
    &-head {
      padding: 15px 30px;
      background: #fff;
      display: flex;
      flex-wrap: wrap;
      border-radius: 0;
      margin-bottom: 15px;
      &.opened {
        .gc-table--row--column {
          &:first-child {
            width: 35%;
            flex: 0 0 35%;
          }
          &:nth-child(2) {
            flex: 0 0 180px;
          }
          &:nth-child(3) {
            flex: 0 0 120px;
          }
          &:nth-child(4) {
            flex: 0 0 120px;
          }
          &:last-child {
            flex: 0 0 235px;
          }
        }
      }
    }
    &-body {
      border-bottom: 1px solid rgb(232, 232, 232);
      background: #fff;
      padding: 15px 15px;
      display: flex;
      flex-wrap: wrap;
      &.selected {
        background: #f9f9f9;
      }
      .gc-table--row--column {
        align-items: center;
        overflow: hidden;
        cursor: default;
        span {
          width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        &:first-child {
          align-items: center;
          padding-top: 5px;
          > div {
            display: flex;
            align-items: center;
          }
          > span {
            color: #191919;
            font-weight: bold;
          }
          .v-image {
            flex: 0 0 auto;
            margin-right: 10px;
            width: 40px !important;
            height: 40px !important;
            border-radius: 50%;
            margin-top: 0;
          }
        }
        &:nth-child(3) {
          span {
            font-size: 14px;
          }
        }
        &:nth-child(4) {
          span {
            font-size: 14px;
          }
          .show-desktop {
            display: block;
          }
          .show-mobile {
            display: none;
          }
        }
        &:nth-child(2) {
          align-items: flex-start;
          flex-direction: column;
          > div {
            display: flex;
            flex-direction: column;
          }
        }
        &:last-child {
          justify-content: flex-end;
          align-items: center;
        }
        > span {
          color: rgb(98, 98, 98);
          font-weight: 500;
          font-size: 16px;
          &:nth-child(1) {
            color: #191919;
            font-weight: bold;
          }
        }
      }
      &.opened {
        .gc-table--row--column {
          padding-right: 10px;
          &:first-child {
            width: 30%;
            flex: 1 0 30%;
          }
          &:nth-child(2) {
            flex: 0 0 180px;
          }
          &:nth-child(3) {
            flex: 0 0 120px;
          }
          &:nth-child(4) {
            display: none;
          }
          &:last-child {
            flex: 0 0 232px;
            padding-right: 0;
            justify-content: flex-end;
          }
        }
      }
      &:last-child {
        border-bottom: none;
      }
    }
  }
}
.absolute-btn.candle {
  position: absolute;
  bottom: -20px;
  right: 15px;
}
.absolute-btn.follow {
  position: absolute;
  bottom: -20px;
  right: 15px;
}
.hovered {
  transition: 0.3s all;
}

@media only screen and (max-width: 1320px) {
  .gc-filters {
    padding: 25px 15px;
    > div {
      box-sizing: border-box;
      padding-right: 20px;
      &:first-child {
        flex: 1 0 35%;
      }
      &:nth-child(2) {
        flex: 1 0 35%;
      }
      &:nth-child(3) {
        flex: 0 0 30%;
      }
      &:nth-child(4) {
        margin-top: 15px;
        flex: 0 0 100%;
      }
    }
  }

  .gc-table {
    padding: 0 0px;
    &--row {
      &-body {
        padding: 15px;
        .gc-table--row--column {
          &:first-child {
            flex: 0 0 35%;
          }
          &:nth-child(4) {
            span {
              font-size: 14px;
            }
          }
        }
        &.opened {
          .gc-table--row--column {
            padding-right: 10px;
            &:first-child {
              width: 30%;
              flex: 1 0 30%;
            }
            &:nth-child(2) {
              flex: 0 0 180px;
            }
            &:nth-child(3) {
              display: none;
            }
            &:nth-child(4) {
              display: none;
            }
            &:last-child {
              flex: 0 0 235px;
            }
          }
        }
      }
    }
  }
}

@media only screen and (max-width: 1100px) {
  .gc-filters {
    padding: 25px 15px;
    > div {
      &:first-child {
        flex: 1 0 50%;
      }
      &:nth-child(2) {
        flex: 1 0 50%;
      }
      &:nth-child(3) {
        margin-top: 15px;
        flex: 0 0 50%;
      }
      &:nth-child(4) {
        margin-top: 15px;
        flex: 0 0 50%;
      }
    }
  }
  .show-fixed-card {
    .display-2-text {
      font-size: 32px !important;
      margin-top: 25px;
      margin-bottom: 5px;
    }
    .display-1-text {
      font-size: 28px !important;
      margin-bottom: 15px;
    }
    .subtitle-2 {
      margin-bottom: 0px !important;
    }
    .title {
      font-size: 18px !important;
    }
  }

  .gc-table {
    p.table-column-title {
      font-size: 14px !important;
      margin-bottom: 3px;
    }
    &--row {
      &--column {
        flex: 0 0 20%;
        padding-right: 10px;
        &:first-child {
          flex: 0 0 250px;
        }
        &:last-child {
          flex: 0 0 235px;
        }
      }
      &-body {
        padding: 15px 15px;
        .gc-table--row--column {
          &:first-child {
            flex: 1 0 250px;
            > div {
              display: flex;
              align-items: center;
            }
          }
          &:nth-child(3) {
            //display: none;
          }
          &:last-child {
            justify-content: flex-end;
            align-items: center;
          }
          > span {
            color: rgb(98, 98, 98);
            font-weight: 500;
            font-size: 16px;
            &:nth-child(1) {
              color: #191919;
              font-weight: bold;
            }
          }
        }
        &.opened {
          .gc-table--row--column {
            padding-right: 10px;
            &:first-child {
              width: 30%;
              flex: 1 0 30%;
            }
            &:nth-child(2) {
              display: none;
            }
            &:nth-child(3) {
              display: none;
            }
            &:last-child {
              flex: 0 0 235px;
              padding-right: 0;
              justify-content: flex-end;
            }
          }
        }
        &:last-child {
          border-bottom: none;
        }
      }
    }
  }
}

@media only screen and (max-width: 910px) {
  .gc-table {
    &--row {
      &--column {
        flex: 0 0 25%;
        padding-right: 10px;
        &:first-child {
          flex: 0 0 250px;
        }
        &:last-child {
          flex: 0 0 235px;
        }
      }
      &-body {
        padding: 15px;
        .gc-table--row--column {
          &:first-child {
            flex: 1 0 250px;
          }
          &:nth-child(3) {
            display: none;
          }
        }
        &.opened {
          .gc-table--row--column {
            padding-right: 10px;
            &:first-child {
              width: 30%;
              flex: 1 0 30%;
            }
            &:nth-child(2) {
              display: none;
            }
            &:nth-child(3) {
              display: none;
            }
          }
        }
      }
    }
  }
}

@media only screen and (max-width: 768px) {
  .gc-table {
    &--row {
      &--column {
        flex: 0 0 25%;
        padding-right: 10px;
        &:first-child {
          flex: 0 0 250px;
        }
        &:last-child {
          flex: 0 0 235px;
        }
      }
      &-body {
        padding: 10px;
        .gc-table--row--column {
          &:first-child {
            flex: 1 0 250px;
          }
          &:nth-child(2) {
            display: none;
          }
          &:nth-child(3) {
            display: none;
          }
        }
        &.opened {
          .gc-table--row--column {
            padding-right: 10px;
            &:first-child {
              width: 30%;
              flex: 1 0 30%;
            }
            &:nth-child(2) {
              display: none;
            }
            &:nth-child(3) {
              display: none;
            }
            &:nth-child(4) {
              display: none !important;
            }
          }
        }
      }
    }
  }
}

@media only screen and (max-width: 660px) {
  .pl-4.show-fixed-card {
    padding-left: 0 !important;
    .rounded-bl-xl {
      border-radius: 0 !important;
    }
  }
  .gc-filters {
    padding: 15px 5px;
    > div {
      padding-right: 0;
      &:first-child {
        flex: 1 0 100%;
        padding-right: 0;
      }
      &:nth-child(2) {
        margin-top: 15px;
        flex: 1 0 100%;
      }
      &:nth-child(3) {
        margin-top: 15px;
        padding-right: 0;
        flex: 1 0 50%;
      }
      &:nth-child(4) {
        margin-top: 15px;
        flex: 0 0 120px;
      }
    }
  }
  .gc-table {
    &--row {
      &--column {
        flex: 0 0 25%;
        padding-right: 10px;
        &:first-child {
          flex: 0 0 250px;
        }
        &:last-child {
          flex: 0 0 235px;
        }
      }
      &-body {
        position: relative;
        padding: 5px;
        .gc-table--row--column {
          p,
          span {
            font-size: 12px !important;
          }
          &:first-child {
            flex: 1 0 calc(70% - 80px);
          }
          &:nth-child(2) {
            display: none;
          }
          &:nth-child(3) {
            display: none;
          }
          &:nth-child(4) {
            flex: 0 0 80px;
            padding-left: 5px;
            text-align: right;
            .table-column-title {
              font-size: 12px !important;
            }
          }
          &:last-child {
            display: none !important;
          }
        }
        &.opened {
          .gc-table--row--column {
            padding-right: 10px;
            &:first-child {
              width: 30%;
              flex: 1 0 30%;
            }
            &:nth-child(2) {
              display: none;
            }
            &:nth-child(3) {
              display: none;
            }
            &:nth-child(4) {
              display: none !important;
            }
          }
        }
      }
    }
  }
  .mobile-icon {
    display: block;
    position: absolute;
    right: 10px;
    top: 30px;
  }
}
@media only screen and (max-width: 660px) {
  .v-dialog {
    .container {
      padding: 0;
    }
  }
  .pl-4.show-fixed-card {
    padding-left: 0 !important;
    .rounded-bl-xl {
      border-radius: 0 !important;
    }
  }
  .gc-filters {
    padding: 15px 5px;
    > div {
      padding-right: 0;
      &:first-child {
        flex: 1 0 100%;
        padding-right: 0;
      }
      &:nth-child(2) {
        margin-top: 15px;
        flex: 1 0 100%;
      }
      &:nth-child(3) {
        margin-top: 15px;
        padding-right: 0;
        flex: 1 0 50%;
      }
      &:nth-child(4) {
        margin-top: 15px;
        flex: 0 0 120px;
      }
    }
  }
  .gc-table {
    &--row {
      &--column {
        flex: 0 0 25%;
        padding-right: 10px;
        &:first-child {
          flex: 0 0 70%;
        }
        &:last-child {
          flex: 0 0 235px;
        }
      }
      &-body {
        position: relative;
        padding: 15px 15px;
        .gc-table--row--column {
          p,
          span {
            font-size: 12px !important;
          }
          &:first-child {
            flex: 1 0 calc(70% - 80px);
          }
          &:nth-child(2) {
            display: none;
          }
          &:nth-child(3) {
            display: none;
          }
          &:nth-child(4) {
            flex: 0 0 80px;
            padding-left: 5px;
            text-align: right;
            .table-column-title {
              font-size: 12px !important;
            }
            .show-desktop {
              display: none;
            }
            .show-mobile {
              display: flex;
              padding-top: 5px;
            }
          }
          &:last-child {
            display: none !important;
          }
        }
        &.opened {
          .gc-table--row--column {
            padding-right: 10px;
            &:first-child {
              width: 30%;
              flex: 1 0 30%;
            }
            &:nth-child(2) {
              display: none;
            }
            &:nth-child(3) {
              display: none;
            }
            &:nth-child(4) {
              display: none !important;
            }
          }
        }
      }
    }
  }
  .mobile-icon {
    display: block;
    position: absolute;
    right: 10px;
    top: 30px;
  }
}
</style>

<style lang="scss" scoped>
.star-icon {
  font-size: 22px;
  position: absolute;
  left: 3px;
  top: 5px;
}
.card-view {
  .star-icon {
    font-size: 30px;
    position: absolute;
    left: 10px;
    top: 10px;
  }
}
@media (max-width: 600px) {
  .mobile-px-0 {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
.inactive {
  background: #f9f9f9 !important;
  color: #bebdbd !important;
}

.rel {
  position: relative;
  width: 100px;
}

.profile-stats {
  flex-wrap: wrap;
  display: flex;
  justify-content: flex-end;
  section {
    width: 100%;
  }
  > div {
    flex: 0 0 25%;
    width: 25%;
    &.full-width {
      width: 100%;
      flex: 0 0 100%;
      > .with-radius {
        width: 150px;
      }
    }
    .v-divider {
      width: 50%;
      margin: auto;
    }
  }
  .full-row {
    flex: 0 0 100%;
  }
  &.edit-mode {
    > div {
      flex: 0 0 33.33%;
      width: 33.33%;
    }
    .full-row {
      flex: 0 0 100%;
    }
  }
  > div.action-form {
    flex: 0 0 100%;
    width: 100%;
    margin-top: 10px;
  }
  p {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    font-size: 14px;
    color: #636363;
    font-weight: 600;
  }
  span.overline {
    color: #c1bfbf;
    font-size: 10px !important;
  }
  &--user {
    justify-content: space-evenly;
    margin-top: 25px;
    width: 100%;
    > div {
      flex: 0 0 33.33%;
      width: 33.33%;
    }
    .display-1 {
      font-size: 22px !important;
    }
    span.overline {
      margin-top: -10px;
      display: block;
    }
  }
}

@media only screen and (max-width: 1050px) {
  .profile-stats {
    justify-content: center;
    > div {
      flex: 0 0 33.33%;
      width: 33.33%;
      .v-divider {
        width: 70%;
        margin: auto;
      }
    }
    p {
      font-size: 16px;
    }
    span.overline {
      font-size: 10px !important;
    }
    &--user {
      margin-top: 25px;
      > div {
        flex: 0 0 33.33%;
        width: 33.33%;
      }
      .display-1 {
        font-size: 22px !important;
      }
      span.overline {
        margin-top: -10px;
      }
    }
  }
}

@media only screen and (max-width: 750px) {
  .profile-stats {
    justify-content: center;
    > div {
      flex: 0 0 50%;
      width: 50%;
      .v-divider {
        width: 80%;
        margin: auto;
      }
    }
    p {
      font-size: 13px;
    }
    span.overline {
      font-size: 9px !important;
    }

    &--user {
      margin-top: 25px;
      > div {
        flex: 0 0 33.33%;
        width: 33.33%;
      }
      .display-1 {
        font-size: 18px !important;
      }
      span.overline {
        margin-top: -10px;
      }
    }
    &.edit-mode {
      margin-top: 25px;
      > div {
        flex: 0 0 100% !important;
        width: 100% !important;
      }
      .display-1 {
        font-size: 18px !important;
      }
      span.overline {
        margin-top: -10px;
      }
    }
  }
}

.show-mobile {
  display: none;
  @media screen and (max-width: 768px) {
    display: block;
  }
}

.show-desktop {
  display: flex;
  @media screen and (max-width: 768px) {
    display: none;
  }
}

.table-container {
  margin: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.responsive-table {
  width: 100%;
}

.table-row {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  gap: 1rem;
  position: relative;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: #f8fafc;
}

.profile-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 2;
}

.profile-pic {
  width: 48px !important;
  height: 48px !important;
  flex: 0 0 48px;
  border-radius: 50%;
  object-fit: cover;
}

.profile-details {
  h3 {
    margin: 0;
    font-size: 1rem;
    color: #1e293b;
  }
  * {
    color: #1e293b;
  }
}

.details-section {
  display: flex;
  flex: 4;
  gap: 2rem;
}

.detail-item {
  flex: 1;
}

.detail-header {
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.detail-content {
  color: #1e293b;
  font-size: 0.875rem;
}

.action-section {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

.action-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  color: #64748b;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.mobile-dates {
  display: none;
  font-size: 0.75rem;
  color: #64748b;
}

@media (max-width: 768px) {
  .table-row {
    padding: 0.75rem;
  }

  .desktop-only {
    display: none;
  }

  .profile-section {
    flex: 1;
  }

  .details-section {
    display: none;
  }

  .mobile-dates {
    display: block;
  }

  .action-section {
    flex: 0;
  }
}

.filter-container {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  // margin-bottom: 2rem;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  align-items: end;
  &.isPet {
    grid-template-columns: repeat(3, 1fr);
    @media screen and (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

.switch-group {
  display: flex;
  margin-bottom: 10px;
  @media screen and (max-width: 768px) {
    margin-bottom: 0;
  }
}

.switch-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
}

.switch-label span {
  transition: color 0.2s ease;
}

.switch-label span.active {
  font-weight: 500;
}

.switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.search-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  @media screen and (max-width: 768px) {
    padding: 0.2rem 1rem;
  }
}

.search-button:hover {
  transform: translateY(-1px);
}

.search-button .mdi {
  font-size: 1.25rem;
}

@media (max-width: 1024px) {
  .filter-grid {
    grid-template-columns: repeat(2, 1fr);
    &.isPet {
      @media screen and (max-width: 768px) {
        grid-template-columns: repeat(1, 1fr);
      }
    }
  }
}

@media (max-width: 640px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .switch-group {
    justify-content: center;
  }

  .search-button {
    width: 100%;
  }
}
</style>